import { useCallback, useEffect, useRef, useState } from 'react'
import { Activity, AlertTriangle, Check, Circle, Minus, Radar } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { adminMutate, Select, usePermissions } from '../admin/primitives.js'
import { Field, SwitchRow, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import { Link } from '../router.js'
import {
  consequencePublication, estConnexion, estListeSources, estProprietaires, estReglagesGeneraux, estEtatSmtp, estSecours,
  fmtDateHeure, lireSupervision, messageEchecMutation, publieSourcesBasculees,
  type ConnexionSonde, type EtatSmtpSonde, type Lecture, type ProprietaireSonde, type ProprietairesSonde,
  type ReglagesGenerauxSonde, type SecoursSonde, type SondeSourceView,
} from './api.js'
import { etapeAlertes, etapeCles, etapeJetons, etapeSmtp, etapeSources, sourcesACle, type Etape } from './parcours-model.js'
import { JetonSondePanel } from './JetonSondePanel.js'
import { SondeCredentialPanel } from './SondeCredentialPanel.js'
import { SondeSmtpCopie } from './SondeSmtpCopie.js'

/**
 * ADMINISTRATION → INTÉGRATIONS → SONDE (21/10/2026).
 *
 * Sonde est le service de supervision technique du club, séparé de Gestion.
 * Gestion reste le seul écran : cette page raccorde Sonde et règle ce qui
 * relève de Gestion — l'adresse, ses deux jetons, ce que Sonde publie, qui
 * surveille chaque source, la recopie du SMTP et les clés des comptes
 * lecteur de Sonde (relayées en écriture seule, Gestion n'en garde rien).
 *
 * Sonde absente ou injoignable : la page RESTE UTILISABLE (adresse et jetons
 * se saisissent sans Sonde) et dit « Sonde n'est pas encore raccordée » ou
 * « Supervision indisponible », en gris, jamais en rouge.
 *
 * Édition : l'adresse et « Ce que Sonde publie » sont des VALEURS → la Save
 * Bar (une unité pour la page, If-Match vers Sonde, 409 → relecture). Jetons,
 * clés, propriétaires et recopie du SMTP sont des GESTES atomiques → des
 * boutons avec confirmation, comme les autres pages de clés.
 */

const ICONE_ETAPE: Record<Etape['etat'], typeof Check> = { fait: Check, en_cours: Activity, a_faire: Circle, inconnu: Minus }
const LIBELLE_ETAPE: Record<Etape['etat'], string> = { fait: 'Fait', en_cours: 'En cours', a_faire: 'À faire', inconnu: '—' }
const ANCRE: Record<Etape['id'], string> = {
  jetons: 'sv-section-jetons', cles: 'sv-section-cles', smtp: 'sv-section-smtp', sources: 'sv-section-sources', alertes: 'sv-section-publie',
}

function Parcours({ etapes }: { etapes: Etape[] }) {
  return (
    <section className="lc-panel sv-section" aria-labelledby="sv-parcours">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="sv-parcours">Raccorder Sonde</h2>
        <span className="lc-hint">chaque case se coche par ce qui s’est réellement passé</span>
      </header>
      <div className="lc-panel__body">
        <ol className="sv-parcours">
          {etapes.map((e, i) => {
            const Icone = ICONE_ETAPE[e.etat]
            return (
              <li key={e.id}>
                <button type="button" className={`sv-etape sv-etape--${e.etat}`}
                        onClick={() => document.getElementById(ANCRE[e.id])?.scrollIntoView({ block: 'start' })}>
                  <span className="sv-etape__tete">
                    <span className="sv-etape__num" aria-hidden="true">{i + 1}</span>
                    <span className="sv-etape__etat"><Icone size={14} aria-hidden="true" /> {LIBELLE_ETAPE[e.etat]}</span>
                  </span>
                  <span className="sv-etape__titre">{e.titre}</span>
                  <span className="sv-etape__detail">{e.detail}</span>
                </button>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}

/** Un message gris : Sonde absente n'est pas une panne. */
function Gris({ l }: { l: Exclude<Lecture<unknown>, { etat: 'ok' }> }) {
  return <p className="cle-verdict" role="status">{l.message}</p>
}

const validerAdresse = (v: string): string | null => {
  if (!v) return 'L’adresse de Sonde est requise (par exemple http://sonde-api:4180).'
  try {
    const u = new URL(v)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'L’adresse doit commencer par http:// ou https://.'
    if (u.username || u.password) return 'L’adresse ne doit contenir ni identifiant ni mot de passe.'
  } catch { return 'Adresse invalide (par exemple http://sonde-api:4180).' }
  return null
}

/** Destinataires de secours saisis « a@b.fr, c@d.fr » → liste propre. */
const listeDestinataires = (v: string): string[] =>
  [...new Set(v.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))]

const validerDestinataires = (v: string): string | null => {
  const l = listeDestinataires(v)
  if (l.length > 3) return 'Trois destinataires de secours au plus.'
  const faux = l.find((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(x))
  return faux ? `Adresse e-mail invalide : ${faux}` : null
}

export function SondeIntegrationPage() {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const peutCles = permissions.includes('sonde.credentials.manage')
  const peutReglages = permissions.includes('sonde.settings.manage')
  const confirm = useConfirm()

  const [connexion, setConnexion] = useState<Lecture<ConnexionSonde> | null>(null)
  const [reglages, setReglages] = useState<Lecture<ReglagesGenerauxSonde> | null>(null)
  const [proprios, setProprios] = useState<Lecture<ProprietairesSonde> | null>(null)
  const [sources, setSources] = useState<Lecture<{ sources: SondeSourceView[] }> | null>(null)
  const [smtp, setSmtp] = useState<Lecture<EtatSmtpSonde> | null>(null)
  const [secours, setSecours] = useState<Lecture<SecoursSonde> | null>(null)
  const [destinataires, setDestinataires] = useState('')

  // Brouillons de la Save Bar (adresse + ce que Sonde publie).
  const [adresse, setAdresse] = useState('')
  const [ombre, setOmbre] = useState<{ shadow_mode: boolean; shadow_release: string } | null>(null)

  // Une relecture (après un jeton, un réglage) ne doit pas écraser une adresse
  // en cours de saisie : le brouillon ne suit le serveur que s'il n'a pas bougé.
  const adresseLue = useRef('')
  const chargerConnexion = useCallback(() => lireSupervision('/connexion', estConnexion).then((l) => {
    setConnexion(l)
    if (l.etat !== 'ok') return
    const lue = l.data.api_url ?? ''
    const precedente = adresseLue.current
    adresseLue.current = lue
    setAdresse((brouillon) => (brouillon === precedente ? lue : brouillon))
  }), [])
  const chargerReglages = useCallback(() => lireSupervision('/settings/general', estReglagesGeneraux).then((l) => {
    setReglages(l)
    setOmbre(l.etat === 'ok' ? { shadow_mode: l.data.shadow_mode, shadow_release: l.data.shadow_release } : null)
  }), [])
  const chargerProprios = useCallback(() => lireSupervision('/owners', estProprietaires).then(setProprios), [])
  const chargerSources = useCallback(() => lireSupervision('/sources', estListeSources).then(setSources), [])
  const chargerSmtp = useCallback(() => lireSupervision('/smtp', estEtatSmtp).then(setSmtp), [])
  const chargerSecours = useCallback(() => lireSupervision('/settings/fallback', estSecours).then((l) => {
    setSecours(l)
    setDestinataires(l.etat === 'ok' ? l.data.recipients.join(', ') : '')
  }), [])
  const toutRelire = useCallback(() => {
    void chargerConnexion(); void chargerReglages(); void chargerProprios(); void chargerSources(); void chargerSmtp(); void chargerSecours()
  }, [chargerConnexion, chargerReglages, chargerProprios, chargerSources, chargerSmtp, chargerSecours])
  useEffect(() => { toutRelire() }, [toutRelire])

  /* ─── Save Bar : adresse + ce que Sonde publie ─── */
  const adresseServeur = connexion?.etat === 'ok' ? connexion.data.api_url ?? '' : ''
  const adresseSale = connexion?.etat === 'ok' && peutCles && adresse.trim() !== adresseServeur
  const reglagesServeur = reglages?.etat === 'ok' ? reglages.data : null
  const ombreSale = peutReglages && reglagesServeur !== null && ombre !== null
    && (ombre.shadow_mode !== reglagesServeur.shadow_mode || ombre.shadow_release !== reglagesServeur.shadow_release)
  const secoursServeur = secours?.etat === 'ok' ? secours.data : null
  const secoursSale = peutReglages && secoursServeur !== null
    && listeDestinataires(destinataires).join(',') !== secoursServeur.recipients.join(',')
  useSaveBar({
    dirty: adresseSale || ombreSale || secoursSale,
    count: Number(adresseSale) + Number(ombreSale) + Number(secoursSale),
    invalid: (adresseSale ? validerAdresse(adresse.trim()) : null) ?? (secoursSale ? validerDestinataires(destinataires) : null),
    onSave: async () => {
      if (adresseSale) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/supervision/connexion', { api_url: adresse.trim() })
        if (r.status >= 300) return messageEchecMutation(r.status, r.data, 'l’adresse n’a pas changé')
        if (estConnexion(r.data)) {
          const lue = r.data.api_url ?? ''
          adresseLue.current = lue
          setConnexion({ etat: 'ok', data: r.data }); setAdresse(lue)
        } else void chargerConnexion()
        // Une nouvelle adresse : tout ce qui vient de Sonde se relit (les
        // réglages après leur propre enregistrement, s'ils sont aussi modifiés).
        if (!ombreSale) void chargerReglages()
        void chargerSources()
      }
      if (ombreSale && reglagesServeur && ombre) {
        const corps: Record<string, unknown> = { etag: reglagesServeur.etag }
        if (ombre.shadow_mode !== reglagesServeur.shadow_mode) corps['shadow_mode'] = ombre.shadow_mode
        if (ombre.shadow_release !== reglagesServeur.shadow_release) corps['shadow_release'] = ombre.shadow_release
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/supervision/settings/general', corps)
        // Seul PRECONDITION_FAILED est un conflit de version : un autre 409
        // (Mode sûr…) porte sa propre raison, dite telle quelle.
        if (r.status === 409 && r.data?.error === 'PRECONDITION_FAILED') {
          void chargerReglages()
          return 'Ces réglages ont été modifiés entre-temps dans Sonde : la page les a relus, refaites votre choix.'
        }
        if (r.status >= 300) return messageEchecMutation(r.status, r.data)
        if (estReglagesGeneraux(r.data)) {
          setReglages({ etat: 'ok', data: r.data })
          setOmbre({ shadow_mode: r.data.shadow_mode, shadow_release: r.data.shadow_release })
        } else void chargerReglages()
        // L'état publié par Sonde (bandeau, parcours) se relit lui aussi.
        void chargerConnexion()
      }
      if (secoursSale && secoursServeur) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/supervision/settings/fallback', {
          etag: secoursServeur.etag, recipients: listeDestinataires(destinataires),
        })
        if (r.status === 409 && r.data?.error === 'PRECONDITION_FAILED') {
          void chargerSecours()
          return 'Les destinataires de secours ont été modifiés entre-temps dans Sonde : la page les a relus, refaites votre saisie.'
        }
        if (r.status >= 300) return messageEchecMutation(r.status, r.data, 'les destinataires n’ont pas changé')
        if (estSecours(r.data)) {
          setSecours({ etat: 'ok', data: r.data }); setDestinataires(r.data.recipients.join(', '))
        } else void chargerSecours()
      }
      return true
    },
    onCancel: () => {
      setAdresse(adresseServeur)
      setOmbre(reglagesServeur ? { shadow_mode: reglagesServeur.shadow_mode, shadow_release: reglagesServeur.shadow_release } : null)
      setDestinataires(secoursServeur ? secoursServeur.recipients.join(', ') : '')
    },
    successMessage: 'Enregistré — Sonde le prend en compte en 10 secondes au plus.',
  })

  /* ─── Qui surveille : gestes atomiques ─── */
  const [raison, setRaison] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [retourProprio, setRetourProprio] = useState<{ ok: boolean; texte: string } | null>(null)
  const changerProprio = async (lignes: { source: string; label: string }[], owner: ProprietaireSonde) => {
    if (lignes.length === 0) return
    const noms = lignes.map((l) => l.label).join(', ')
    const versSonde = owner === 'sonde'
    const ok = await confirm(versSonde ? {
      title: lignes.length > 1 ? `Confier ${lignes.length} sources à Sonde ?` : `Confier ${noms} à Sonde ?`,
      message: 'Gestion n’interrogera plus ces équipements et ne préviendra plus pour eux : c’est Sonde qui prévient, selon « Ce que Sonde publie ». '
        + 'Si Sonde s’arrête, l’écran affichera « Supervision indisponible » en gris, et personne ne sera prévenu pour ces sources jusqu’à son retour '
        + 'ou jusqu’à ce qu’elles soient rendues à Gestion.',
      details: [{ label: 'Sources', value: noms }, { label: 'Sonde publie', value: libellePublie }],
      variant: 'sensitive',
      confirmLabel: 'Confier à Sonde',
    } : {
      title: lignes.length > 1 ? `Rendre ${lignes.length} sources à Gestion ?` : `Rendre ${noms} à Gestion ?`,
      message: 'Gestion recommence à interroger ces équipements lui-même et prévient comme avant. Sonde cesse de prévenir pour elles.',
      details: [{ label: 'Sources', value: noms }],
      variant: 'sensitive',
      confirmLabel: 'Rendre à Gestion',
    })
    if (!ok) return
    setEnCours(true); setRetourProprio(null)
    try {
      const corps: Record<string, unknown> = { sources: lignes.map((l) => l.source), owner }
      if (raison.trim()) corps['reason'] = raison.trim()
      type Reponse = { error?: string; message?: string; details?: { rules?: { name: string; service: string }[] } }
      let r = await adminMutate<Reponse>('PUT', '/api/v1/supervision/owners', corps)
      // Des règles posées à la main se tairaient : on les montre, l'auteur décide.
      if (r.status === 409 && r.data?.error === 'SONDE_RULES_SILENCED') {
        const regles = r.data.details?.rules ?? []
        const accepte = await confirm({
          title: regles.length > 1 ? `${regles.length} règles ne préviendront plus` : 'Une règle ne préviendra plus',
          message: 'Ces règles posées à la main portent uniquement sur les sources confiées : elles se taisent, et Sonde ne les connaît pas. '
            + 'Un seuil ou un sujet qui leur est propre n’aura plus personne pour prévenir.',
          details: regles.slice(0, 8).map((x) => ({ label: x.service, value: x.name })),
          variant: 'sensitive',
          confirmLabel: 'Confier quand même',
        })
        if (!accepte) { setRetourProprio({ ok: false, texte: 'Bascule annulée — rien n’a été modifié.' }); return }
        r = await adminMutate<Reponse>('PUT', '/api/v1/supervision/owners', { ...corps, accept_silenced_rules: true })
      }
      if (r.status >= 200 && r.status < 300) {
        if (estProprietaires(r.data)) setProprios({ etat: 'ok', data: r.data }); else void chargerProprios()
        setRaison('')
        setRetourProprio({ ok: true, texte: versSonde ? `${noms} : Sonde surveille désormais, Gestion n’interroge plus.` : `${noms} : Gestion surveille de nouveau.` })
        void chargerConnexion()
        return
      }
      setRetourProprio({ ok: false, texte: messageEchecMutation(r.status, r.data) })
    } catch {
      setRetourProprio({ ok: false, texte: 'Gestion n’a pas répondu : relisez la page avant de réessayer.' })
    } finally { setEnCours(false) }
  }

  // « Alertes publiées » AVANT « Sources confiées » : c'est l'ordre du geste.
  const etapes = [etapeJetons(connexion), etapeCles(sources), etapeSmtp(smtp, secours), etapeAlertes(connexion), etapeSources(proprios)]
  const c = connexion?.etat === 'ok' ? connexion.data : null
  // Confier une source n'a de sens que si Sonde publie les sources basculées
  // (lu dans SON état, pas dans un brouillon) ; le serveur le vérifie aussi.
  const publie = c && c.status.reachable ? publieSourcesBasculees(c.status.shadow_mode, c.status.shadow_release) : null
  const libellePublie = c?.status.shadow_mode === false ? 'Tout (mode ombre coupé)'
    : reglagesServeur?.shadow_release_options.find((o) => o.value === c?.status.shadow_release)?.label ?? c?.status.shadow_release ?? '—'
  const confiees = proprios?.etat === 'ok' ? proprios.data.owners.filter((o) => o.owner === 'sonde').length : 0
  const enTete = !c ? { pill: 'lc-pill--idle', texte: '—' }
    : !c.configured ? { pill: 'lc-pill--idle', texte: 'Pas encore raccordée' }
      : c.status.reachable ? { pill: 'lc-pill--ok', texte: 'Raccordée' }
        : { pill: 'lc-pill--idle', texte: 'Supervision indisponible' }
  const options = reglagesServeur?.shadow_release_options ?? []
  const libelleOption = options.find((o) => o.value === ombre?.shadow_release)?.label

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page cle-page sv-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title cle-titre"><Radar size={22} aria-hidden="true" /> Sonde — supervision technique</h1>
            <p className="lc-subtitle cle-sous">
              <span className={`lc-pill ${enTete.pill}`}>{enTete.texte}</span>
              <span>Sonde surveille les équipements du club ; Gestion reste le seul écran et le seul à prévenir les équipes.</span>
            </p>
            <p className="lc-hint">Vue d’ensemble de la supervision : <Link href="/settings/supervision">Paramètres → Supervision</Link></p>
          </div>
        </header>

        <Parcours etapes={etapes} />

        {/* ─── État et adresse ─── */}
        <section className="lc-panel sv-section" aria-labelledby="sv-etat">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-etat">État de Sonde</h2></header>
          <div className="lc-panel__body cle-corps">
            {connexion && connexion.etat !== 'ok' ? <Gris l={connexion} /> : null}
            {c ? (
              <>
                {!c.configured ? <p className="cle-verdict" role="status">Sonde n’est pas encore raccordée : saisissez son adresse et les deux jetons ci-dessous.</p> : null}
                {c.configured && !c.status.reachable ? (
                  <p className="cle-verdict" role="status">Supervision indisponible{c.status.message ? ` : ${c.status.message}` : ' : Sonde ne répond pas pour l’instant.'}</p>
                ) : null}
                <dl className="cle-faits">
                  <div><dt>Version de Sonde</dt><dd>{c.status.version ?? '—'}</dd></div>
                  <div><dt>Joignable</dt><dd>{!c.configured ? '—' : c.status.reachable ? 'Oui' : 'Non'}</dd></div>
                  <div>
                    <dt>Contrat d’API</dt>
                    <dd>
                      {c.contract.aligned === null ? '—'
                        : c.contract.aligned ? 'Aligné : Gestion et Sonde parlent la même version.'
                          : 'Sonde plus récente : recopier le contrat dans Gestion avant d’utiliser ses nouveautés.'}
                      {c.contract.aligned === false ? (
                        <span className="sv-mono sv-bloc">Gestion {c.contract.local_sha256.slice(0, 12)} · Sonde {c.contract.remote_sha256?.slice(0, 12) ?? '—'}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Mode ombre</dt>
                    <dd>{c.status.shadow_mode === null ? '—' : c.status.shadow_summary_fr ?? (c.status.shadow_mode ? 'Sonde observe sans alerter.' : 'Coupé : Sonde publie ses alertes.')}</dd>
                  </div>
                </dl>
                {peutCles ? (
                  <div className="cle-saisie">
                    <Field label="Adresse de Sonde" helper="Adresse interne au serveur, jamais exposée au navigateur. Changer d’adresse demande la confirmation forte."
                           error={adresseSale ? validerAdresse(adresse.trim()) : null}>
                      <TextInput value={adresse} onChange={setAdresse} placeholder="http://sonde-api:4180" ariaLabel="Adresse de Sonde" />
                    </Field>
                  </div>
                ) : (
                  <dl className="cle-faits"><div><dt>Adresse</dt><dd className="sv-mono">{c.api_url ?? '—'}</dd></div></dl>
                )}
              </>
            ) : null}
          </div>
        </section>

        {/* ─── Jetons ─── */}
        <div id="sv-section-jetons" className="sv-deux">
          <JetonSondePanel kind="api" etat={c?.tokens.api ?? null} peutModifier={peutCles} onEnregistre={() => { void chargerConnexion(); void chargerReglages(); void chargerSources() }} />
          <JetonSondePanel kind="worker" etat={c?.tokens.worker ?? null} peutModifier={peutCles} onEnregistre={() => { void chargerConnexion() }} />
        </div>

        {/* ─── Ce que Sonde publie ─── */}
        <section id="sv-section-publie" className="lc-panel sv-section" aria-labelledby="sv-publie">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-publie">Ce que Sonde publie</h2></header>
          <div className="lc-panel__body cle-corps">
            {reglages && reglages.etat !== 'ok' ? <Gris l={reglages} /> : null}
            {reglagesServeur && ombre ? (
              <>
                <p className="sv-conseq">{consequencePublication(ombre.shadow_mode, ombre.shadow_release, libelleOption, confiees)}</p>
                <SwitchRow
                  checked={ombre.shadow_mode}
                  onChange={(v) => setOmbre({ ...ombre, shadow_mode: v })}
                  title="Mode ombre"
                  desc="Sonde observe sans alerter, sauf ce que le choix ci-dessous publie quand même."
                  disabled={!peutReglages}
                />
                {ombre.shadow_mode ? (
                  <div className="sv-champ-court">
                    {peutReglages ? (
                      <Select
                        label="Ce que Sonde publie quand même"
                        value={ombre.shadow_release}
                        options={options.map((o) => ({ value: o.value, label: o.label }))}
                        onChange={(v) => setOmbre({ ...ombre, shadow_release: v })}
                      />
                    ) : (
                      <dl className="cle-faits"><div><dt>Publié quand même</dt><dd>{libelleOption ?? '—'}</dd></div></dl>
                    )}
                  </div>
                ) : null}
                <p className="lc-hint">
                  Réglage sensible : son enregistrement demande la confirmation forte.
                  {ombre.shadow_mode
                    ? ' En mode ombre, une alerte que Gestion envoie déjà, pour une source encore surveillée par Gestion, ne part jamais deux fois.'
                    : ' Mode ombre coupé : Sonde publie tout, y compris ce que Gestion envoie encore pour ses propres sources — doublons possibles tant que toutes ne sont pas confiées.'}
                </p>
              </>
            ) : null}
          </div>
        </section>

        {/* ─── Qui surveille ─── */}
        <section id="sv-section-sources" className="lc-panel sv-section" aria-labelledby="sv-qui">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="sv-qui">Qui surveille</h2>
            {peutReglages && proprios?.etat === 'ok' ? (
              <button type="button" className="lc-btn" disabled={enCours || publie !== true || proprios.data.owners.every((o) => o.owner === 'sonde')}
                      onClick={() => { void changerProprio(proprios.data.owners.filter((o) => o.owner !== 'sonde'), 'sonde') }}>
                Tout confier à Sonde
              </button>
            ) : null}
          </header>
          <div className="lc-panel__body cle-corps">
            {proprios && proprios.etat !== 'ok' ? <Gris l={proprios} /> : null}
            {proprios?.etat === 'ok' ? (
              <>
                <p className="lc-hint cle-intro">
                  Pour une source confiée à Sonde, Gestion n’interroge plus l’équipement : il affiche ce que Sonde voit, et c’est Sonde qui prévient.
                  Le retour arrière est immédiat.
                </p>
                {peutReglages && publie !== true ? (
                  <p className="cle-alerte" role="note">
                    <AlertTriangle size={15} aria-hidden="true" />{' '}
                    <span>
                      {publie === false
                        ? 'Sonde est en ombre complète : réglez d’abord « Ce que Sonde publie » (au moins « Les sources déjà basculées »). Sinon, personne ne préviendrait pour les sources confiées.'
                        : 'Ce que Sonde publie est illisible pour l’instant : aucune source ne peut lui être confiée.'}
                    </span>
                  </p>
                ) : null}
                {proprios.data.warnings.map((w) => (
                  <p key={w} className="cle-alerte" role="note"><AlertTriangle size={15} aria-hidden="true" /> <span>{w}</span></p>
                ))}
                <ul className="sv-lignes">
                  {proprios.data.owners.map((o) => (
                    <li key={o.source} className="sv-ligne">
                      <span className="sv-ligne__corps">
                        <span className="sv-ligne__titre">{o.label}</span>
                        <span className="sv-ligne__meta">
                          {o.changed_at ? `Depuis le ${fmtDateHeure(o.changed_at)}${o.changed_by_name ? `, par ${o.changed_by_name}` : ''}${o.reason ? ` — ${o.reason}` : ''}` : 'Jamais changé'}
                        </span>
                      </span>
                      <span className={`sv-proprio sv-proprio--${o.owner}`}>{o.owner === 'sonde' ? 'Sonde surveille' : 'Gestion surveille'}</span>
                      {peutReglages ? (
                        <button type="button" className="lc-btn lc-btn--ghost" disabled={enCours || (o.owner !== 'sonde' && publie !== true)}
                                onClick={() => { void changerProprio([o], o.owner === 'sonde' ? 'gestion' : 'sonde') }}>
                          {o.owner === 'sonde' ? 'Rendre à Gestion' : 'Confier à Sonde'}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {peutReglages ? (
                  <Field label="Raison du changement (facultatif)" helper="Notée dans l’audit avec le changement.">
                    <TextInput value={raison} onChange={setRaison} maxLength={500} ariaLabel="Raison du changement de surveillance" placeholder="Première soirée sous Sonde" />
                  </Field>
                ) : null}
                {retourProprio ? <p role="status" className={`cle-verdict ${retourProprio.ok ? 'cle-verdict--ok' : 'cle-verdict--ko'}`}>{retourProprio.texte}</p> : null}
              </>
            ) : null}
          </div>
        </section>

        {/* ─── Secours e-mail ─── */}
        <section id="sv-section-smtp" className="lc-panel sv-section" aria-labelledby="sv-smtp">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-smtp">Secours e-mail</h2></header>
          <div className="lc-panel__body cle-corps">
            <SondeSmtpCopie variante="panneau" />
            {secours && secours.etat !== 'ok' && secours.etat !== 'refus' ? <Gris l={secours} /> : null}
            {secoursServeur ? (
              <>
                {secoursServeur.recipients.length === 0 ? (
                  <p className="cle-alerte" role="note">
                    <AlertTriangle size={15} aria-hidden="true" />{' '}
                    <span>Aucun destinataire de secours : si Gestion se tait pendant une panne, la sentinelle de Sonde ne peut prévenir personne.</span>
                  </p>
                ) : null}
                {peutReglages ? (
                  <Field label="Destinataires de secours (3 au plus)"
                         helper="Copiés dans Sonde : elle doit pouvoir écrire sans Gestion. Séparez les adresses par une virgule. Enregistrement avec confirmation forte."
                         error={secoursSale ? validerDestinataires(destinataires) : null}>
                    <TextInput value={destinataires} onChange={setDestinataires} placeholder="regie@liveclubrennes.com" ariaLabel="Destinataires de secours" />
                  </Field>
                ) : (
                  <dl className="cle-faits"><div><dt>Destinataires de secours</dt><dd>{secoursServeur.recipients.length > 0 ? secoursServeur.recipients.join(', ') : '—'}</dd></div></dl>
                )}
              </>
            ) : null}
          </div>
        </section>

        {/* ─── Clés des comptes lecteur ─── */}
        <section id="sv-section-cles" className="sv-section sv-cles" aria-labelledby="sv-cles">
          <h2 className="lc-section-title" id="sv-cles">Clés des comptes lecteur</h2>
          <p className="lc-hint cle-intro">
            Sonde lit chaque équipement avec son PROPRE compte, en lecture seule — distinct de celui de Gestion. Saisir sa clé ici ne
            remplace jamais une clé de Gestion. Gestion la relaie à Sonde et n’en garde rien.
          </p>
          {sources?.etat === 'refus' ? <p className="cle-verdict" role="status">Votre compte ne voit pas le détail des sources de Sonde.</p> : null}
          {sources && sources.etat !== 'ok' && sources.etat !== 'refus' ? <Gris l={sources} /> : null}
          {sources?.etat === 'ok' ? sourcesACle(sources.data.sources).map((s) => (
            <SondeCredentialPanel key={s.code} source={s} />
          )) : null}
        </section>
      </main>
    </Shell>
  )
}
